import React, { useEffect, useState } from "react";
import { Plus, FolderOpen, Trash, FilmStrip } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Workspace() {
  const [projects, setProjects] = useState([]);
  const [show, setShow] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [platform, setPlatform] = useState("tiktok");

  const load = () => api.get("/projects").then(r => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    await api.post("/projects", { name, description: desc, platform });
    toast.success("Project created");
    setShow(false); setName(""); setDesc("");
    load();
  };

  const del = async (id) => {
    await api.delete(`/projects/${id}`);
    toast.success("Deleted");
    load();
  };

  const folders = Array.from(new Set(projects.map(p => p.folder)));

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto" data-testid="workspace-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-[#FF3B6B] text-sm font-mono">// CREATOR WORKSPACE</p>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tighter mt-2">Your projects</h1>
          <p className="text-white/50 mt-1">{projects.length} total · {folders.length} folder(s)</p>
        </div>
        <button onClick={() => setShow(true)} className="btn-primary px-5 py-2.5 rounded-lg font-medium flex items-center gap-2" data-testid="new-project-btn">
          <Plus weight="bold" size={16} /> New project
        </button>
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {projects.length === 0 && (
          <div className="md:col-span-3 glass rounded-xl p-12 text-center text-white/40 border-dashed">
            <FolderOpen size={48} className="mx-auto mb-3 text-white/20" />
            <p className="mb-3">No projects yet</p>
            <button onClick={() => setShow(true)} className="btn-primary px-4 py-2 rounded-md text-sm">Create your first</button>
          </div>
        )}
        {projects.map((p) => (
          <div key={p.id} className="glass rounded-xl p-5 hover:border-white/30 transition-all group" data-testid={`project-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-md bg-[#FF3B6B]/10 border border-[#FF3B6B]/30 flex items-center justify-center">
                <FilmStrip className="text-[#FF3B6B]" size={20} />
              </div>
              <button onClick={() => del(p.id)} className="text-white/30 hover:text-[#FF3B6B] opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`delete-project-${p.id}`}>
                <Trash size={16} />
              </button>
            </div>
            <h3 className="mt-3 font-semibold">{p.name}</h3>
            <p className="text-sm text-white/50 mt-1 line-clamp-2">{p.description || "No description"}</p>
            <div className="mt-4 flex items-center justify-between text-xs">
              <span className="capitalize px-2 py-0.5 bg-white/5 rounded">{p.platform}</span>
              <span className="text-white/40 capitalize">{p.status}</span>
            </div>
          </div>
        ))}
      </div>

      {show && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-6" onClick={() => setShow(false)} data-testid="new-project-modal">
          <div onClick={(e) => e.stopPropagation()} className="glass-strong rounded-2xl p-8 w-full max-w-md">
            <h2 className="text-2xl font-bold tracking-tighter">New project</h2>
            <div className="mt-6 space-y-4">
              <div>
                <label className="text-xs text-white/60 uppercase tracking-wider">Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-[#FF3B6B] focus:outline-none" data-testid="project-name-input" />
              </div>
              <div>
                <label className="text-xs text-white/60 uppercase tracking-wider">Description</label>
                <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={3} className="mt-2 w-full bg-white/5 border border-white/10 rounded-lg p-3 text-sm focus:border-[#FF3B6B] focus:outline-none resize-none" data-testid="project-desc-input" />
              </div>
              <div>
                <label className="text-xs text-white/60 uppercase tracking-wider">Platform</label>
                <Select value={platform} onValueChange={setPlatform}>
                  <SelectTrigger className="mt-2 bg-white/5 border-white/10 capitalize" data-testid="project-platform-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {["tiktok", "youtube", "instagram"].map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setShow(false)} className="flex-1 py-2.5 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 text-sm" data-testid="cancel-project-btn">Cancel</button>
                <button onClick={create} className="flex-1 btn-primary py-2.5 rounded-lg text-sm font-semibold" data-testid="save-project-btn">Create</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
